/**
 * ChatScoreBreakdownCard — inline component-score breakdown for the
 * `get_analysis` / `get_full_deal_context` tool results.
 *
 * Reuses the analysis-page scoring config (buildCompScores + COMPS + getSummary)
 * and renders a vertical-row card matching ListDealsCard (bordered surface-card,
 * divide-y rows, ScoreDisplay on the right). Per-component finding prefers the
 * pre-generated TermSheetNarrative interpretation surfaced on the tool result
 * (component_interpretations[key]); falls back to the deterministic getSummary
 * one-liner. Weight is intentionally omitted — internal plumbing.
 *
 * When no score exists (analysis not available / no component detail) the card
 * renders nothing and the model's prose carries the "no score yet" message,
 * preserving "components render OR prose carries, never both".
 *
 * HANDOFF_CHAT_INSIGHT_CARDS — visual cards pass.
 */
import { ChevronRight } from 'react-feather';

import { buildCompScores, type CompScore } from '../../../components/analysis/CompCard/compConfig';
import { ScoreDisplay } from '../../../components/ui';
import type { Analysis } from '../../../api/types';
import { useChatStore } from '../../../stores/chatStore';
import { getComponentInsight } from '../lib/componentDetail';
import type { ToolResultProps } from '../lib/component-registry';

interface BreakdownResult {
  analysis_available?: boolean;
  component_details_available?: boolean;
  overall_score?: number | null;
  component_interpretations?: Record<string, string> | null;
}

export function ChatScoreBreakdownCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as BreakdownResult & Partial<Analysis>;

  // No score / breakdown available — let the prose carry the message.
  if (
    r.analysis_available === false ||
    r.component_details_available === false ||
    !r.component_details
  ) {
    return null;
  }

  const comps = buildCompScores(r as Analysis);
  if (comps.length === 0) {
    return null;
  }

  const interpretations = r.component_interpretations ?? {};
  const overall =
    typeof r.overall_score === 'number' ? Math.round(r.overall_score) : null;

  // Card-relevant extraction subset — present on both get_analysis (after the
  // backend enrich) and get_full_deal_context. Drives the detail cards.
  const fields = (r as { fields?: Record<string, unknown> | null }).fields ?? null;
  // Namespaces the detail card + dedupe so two deals' breakdowns in one session
  // don't collide on a bare compKey (get_analysis → term_sheet_id;
  // get_full_deal_context → resolved_term_sheet_id).
  const tsId =
    (r as { term_sheet_id?: number | string }).term_sheet_id ??
    (r as { resolved_term_sheet_id?: number | string }).resolved_term_sheet_id ??
    null;

  // Click a component row → inject a standalone ComponentDetailCard (+ the
  // pre-generated narrative below it). Deduped by (term sheet, compKey): a
  // re-click scrolls to the existing card with a brief highlight pulse.
  const handleSelect = (c: CompScore) => {
    const store = useChatStore.getState();
    const already = store.messages.some((m) =>
      m.toolResults?.some((tr) => {
        if (tr.tool_name !== 'component_detail') {
          return false;
        }
        const res = tr.result as { compKey?: string; tsId?: number | string | null } | null;
        return res?.compKey === c.key && res?.tsId === tsId;
      }),
    );
    if (already) {
      const el = document.getElementById(`detail-card-${tsId ?? 'x'}-${c.key}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Restart the highlight pulse even if the class is still applied.
        el.classList.remove('detail-card-pulse');
        el.getBoundingClientRect(); // force reflow
        el.classList.add('detail-card-pulse');
        window.setTimeout(() => el.classList.remove('detail-card-pulse'), 700);
      }
      return;
    }
    store.appendMessage({
      id: '',
      role: 'assistant',
      content: '',
      toolResults: [
        {
          tool_name: 'component_detail',
          result: { compKey: c.key, tsId, fields, details: c.details, score: c.score },
        },
      ],
    });
    const narrative = (
      interpretations[c.key] ?? interpretations[c.key === 'fee_load' ? 'fees' : c.key]
    )?.trim();
    if (narrative) {
      store.appendMessage({ id: '', role: 'assistant', content: narrative });
    }
  };

  return (
    <div className="mt-2 border border-border bg-surface-card">
      {overall !== null && (
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <p className="text-body-sm font-semibold text-txt-primary">Score breakdown</p>
          <ScoreDisplay score={overall} size="sm" showLabel={false} />
        </div>
      )}
      <div className="divide-y divide-border">
        {comps.map((c) => {
          const stored = interpretations[c.key]?.trim();
          // Compliant deterministic fallback (positional, never P-notation or
          // evaluative) when no stored interpretation exists. getSummary stays
          // on the analysis page only — it emits "P42"/"Favorable" which are
          // banned in borrower-facing chat.
          const finding =
            stored || (c.score !== null ? getComponentInsight(c.key, fields, c.details) : '');
          const Icon = c.Icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => handleSelect(c)}
              aria-label={`Show ${c.label} detail`}
              className="flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange"
            >
              <span className="mt-0.5 shrink-0 text-txt-tertiary">
                <Icon size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-body font-semibold text-txt-primary">{c.label}</p>
                {finding && <p className="mt-0.5 text-body-sm text-txt-secondary">{finding}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {c.score !== null ? (
                  <ScoreDisplay score={Math.round(c.score)} size="sm" showLabel={false} />
                ) : (
                  <span className="text-caption uppercase text-txt-tertiary">Not scored</span>
                )}
                <ChevronRight size={14} className="text-txt-tertiary" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
